import { useQuery } from "@tanstack/react-query";

import { createMutationHook } from "~/shared/api/query-factories";

import { createElement, deleteElement, listElements, updateElement } from "./api";
import type {
    Element,
    ElementCreateInput,
    ElementDeleteInput,
    ElementListResponse,
    ElementUpdateInput,
} from "./types";

export const elementKeys = {
    all: ["v2", "elements"] as const,
    list: (workspaceId: string | undefined) => ["v2", "elements", "list", workspaceId ?? ""] as const,
};

/**
 * Библиотека элементов активного workspace. Список приходит целиком (бэк без пагинации),
 * поэтому кэшируем его одним ключом на пространство и режем на страницы уже в view-model.
 */
export function useWorkspaceElements(workspaceId: string | undefined, options?: { enabled?: boolean }) {
    return useQuery<ElementListResponse>({
        queryKey: elementKeys.list(workspaceId),
        queryFn: () => listElements(workspaceId!),
        enabled: (options?.enabled ?? true) && !!workspaceId,
        staleTime: 30_000,
    });
}

export const useCreateElement = createMutationHook<Element, ElementCreateInput>({
    mutationFn: createElement,
    invalidate: [elementKeys.all],
});

export const useUpdateElement = createMutationHook<Element, ElementUpdateInput>({
    mutationFn: updateElement,
    invalidate: [elementKeys.all],
});

export const useDeleteElement = createMutationHook<void, ElementDeleteInput>({
    mutationFn: deleteElement,
    invalidate: [elementKeys.all],
});
